import { useCallback, useState } from 'react';
import type { UserEntity } from '@user/UserEntity';
import { FlatButton } from '@framework/ui/atoms';
import { Modal } from '@framework/ui/elements';
import { toast } from 'react-hot-toast';
import { trpcClient } from '@framework/trpcClient';
import { FirebaseAuthenticationClient } from '@framework/firebase/auth';
import { useTranslation } from 'react-i18next';

type Props = {
  isOpen: boolean;
  onClose(): void;
  userEntity: UserEntity;
  // 解除成功後に連携状態(providerData)を最新化するためのコールバック
  onDisconnected(): Promise<void>;
};

/**
 * ログイン中ユーザーの Google アカウント連携を解除するモーダル。
 * バックエンド API で解除可否の検証と記録を行った後、Firebase Auth 上の unlink はクライアント SDK で実行する。
 *
 * パスワード未設定時はサーバー側でも拒否されるが、UI でも事前にボタンを無効化する。
 */
export const DisconnectGoogleModal: React.FC<Props> = ({ isOpen, onClose, userEntity, onDisconnected }: Props) => {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleClose = useCallback(() => {
    setProcessing(false);
    setError('');
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    setProcessing(true);
    setError('');

    try {
      // バックエンドで解除可否の検証(パスワード設定チェック)と記録(アクションログ)を行う
      const { result } = await trpcClient.user.auth.disconnectGoogle.mutate();

      if (result === 'password-not-set') {
        setError(t('auth.googleDisconnect.modal.noPasswordWarning'));
        return;
      }

      // Firebase Auth 上の unlink はクライアント SDK で行う。
      // (Admin SDK での unlink はトークン失効を伴い、ログイン中のユーザーが強制サインアウトされるため)
      await FirebaseAuthenticationClient.unlinkGoogle();

      await onDisconnected();
      toast.success(t('auth.googleDisconnect.modal.disconnectSuccess'));
      handleClose();
    } catch (error) {
      console.error(error);
      setError(t('auth.googleDisconnect.modal.disconnectError'));
    } finally {
      setProcessing(false);
    }
  }, [handleClose, onDisconnected, t]);

  const noPassword = !userEntity.hasPassword;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="flex flex-col">
      {/* header */}
      <div className="px-8 pb-4 pt-8 text-2xl font-bold">{t('auth.googleDisconnect.modal.title')}</div>
      <hr className="w-full" />

      {/* content */}
      <div className="flex flex-col p-8">
        <div>{t('auth.googleDisconnect.modal.description')}</div>

        <div className="py-4 text-center">
          <FlatButton
            color="brand"
            onClick={handleSubmit}
            className="px-8"
            disabled={noPassword || processing}
            loading={processing}
          >
            {t('auth.googleDisconnect.modal.submit')}
          </FlatButton>
        </div>

        {noPassword && <div className="text-red-700">{t('auth.googleDisconnect.modal.noPasswordWarning')}</div>}

        {error && <div className="pt-8 text-red-700">{error}</div>}
      </div>

      {/* footer */}
      <hr className="w-full" />
      <div className="flex justify-end px-8 pb-8 pt-4">
        <FlatButton onClick={handleClose}>{t('common.action.close')}</FlatButton>
      </div>
    </Modal>
  );
};
